import { useWindowVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  DataHistory,
  Div,
  P,
  centsToDollars,
  formatter,
  lightTheme,
  useIsMobile,
  virtualizerStyle,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { OrderComponentV2 } from './OrderComponentV2';

type HistoryVirtualRow =
  | {
      type: 'dayHeader';
      dayIndex: number;
    }
  | {
      type: 'order';
      dayIndex: number;
      orderIndex: number;
    };

export const OrderHistoryComponent = ({
  dataHistory,
  isStatusCancelled,
  onBeforeNavigate,
}: {
  dataHistory: DataHistory[];
  isStatusCancelled: boolean;
  onBeforeNavigate?: () => void;
}) => {
  const isMobile = useIsMobile();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  const rows: HistoryVirtualRow[] = useMemo(() => {
    const flat: HistoryVirtualRow[] = [];
    dataHistory.forEach((historyDay, dayIndex) => {
      if (!historyDay?.orders || historyDay.orders.length === 0) return;

      flat.push({ type: 'dayHeader', dayIndex });
      historyDay.orders.forEach((_, orderIndex) => {
        flat.push({ type: 'order', dayIndex, orderIndex });
      });
    });
    return flat;
  }, [dataHistory]);

  useLayoutEffect(() => {
    setScrollMargin(listRef.current?.offsetTop ?? 0);
  }, [rows.length]);

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: (index) =>
      rows[index]?.type === 'dayHeader' ? 40 : isMobile ? 204 : 74,
    overscan: 15,
    scrollMargin,
  });

  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [isMobile, rowVirtualizer]);

  return (
    <Div ref={listRef}>
      <Div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;

          if (row.type === 'dayHeader') {
            const historyDay = dataHistory[row.dayIndex];
            const virtualizerStyleValue = virtualizerStyle(
              virtualRow,
              rowVirtualizer
            );
            return (
              <Div
                key={`day-${historyDay.salesDay}`}
                style={virtualizerStyleValue as any}
              >
                <Box
                  $borderRadius="16px"
                  $p="0 20px"
                  $pSM="0 16px"
                  $flex
                  $between
                  $alignCenter
                  $mb="8"
                  $background={
                    isStatusCancelled ? lightTheme.white : lightTheme.primary
                  }
                  $border={`1px solid ${
                    isStatusCancelled
                      ? lightTheme.lightGray
                      : lightTheme.primary
                  }`}
                  $boxShadow={
                    isStatusCancelled ? lightTheme.shadows.shadow1 : 'none'
                  }
                  style={{
                    minHeight: '32px',
                  }}
                >
                  <P
                    $bold
                    $m="0"
                    $color={
                      isStatusCancelled ? lightTheme.darkGray : lightTheme.white
                    }
                  >
                    {DateTime.fromISO(historyDay.salesDay).toFormat(
                      'MMM dd, yyyy'
                    )}
                  </P>
                  <Div $flex $alignCenter $gap="20px" $gapSM="12px">
                    <P
                      $m="0"
                      $color={
                        isStatusCancelled
                          ? lightTheme.darkGray
                          : lightTheme.white
                      }
                    >
                      <b>
                        {formatter.format(centsToDollars(historyDay.total))}
                      </b>{' '}
                      ({historyDay.count} orders)
                    </P>
                  </Div>
                </Box>
              </Div>
            );
          }

          const historyDay = dataHistory[row.dayIndex];
          const order = historyDay.orders?.[row.orderIndex];
          if (!order) return null;

          const virtualizerStyleValue = virtualizerStyle(
            virtualRow,
            rowVirtualizer
          );

          return (
            <Div key={order.id} style={virtualizerStyleValue as any}>
              <OrderComponentV2
                data={order}
                index={row.orderIndex}
                onBeforeNavigate={onBeforeNavigate}
              />
            </Div>
          );
        })}
      </Div>
    </Div>
  );
};
